export type BatchItemResult<Result> =
  | { ok: true; value: Result }
  | { ok: false; error: unknown };

export type BatchSchedulerOptions = {
  // Quiet period after the latest request before a batch is sent.
  debounceMilliseconds: number;
  // Upper bound for the debounce, measured from the first queued request,
  // so a steady trickle of requests can't postpone the batch forever.
  maxDelayMilliseconds: number;
  maxBatchSize: number;
};

type PendingRequest<Item, Result> = {
  item: Item;
  resolve: (value: Result) => void;
  reject: (error: unknown) => void;
};

// Groups requests made in quick succession into batches.
// Sending rules:
// - debounce (extended per request, capped by maxDelay)
// - at most one batch in flight; requests arriving meanwhile are sent
//   right after it returns, without another debounce
// Rate limiting is left to the backend.
export class BatchScheduler<Item, Result> {
  private queue: PendingRequest<Item, Result>[] = [];
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private firstQueuedAt: number | null = null;
  private isInFlight = false;

  constructor(
    private send: (items: Item[]) => Promise<BatchItemResult<Result>[]>,
    private options: BatchSchedulerOptions
  ) {}

  request(item: Item): Promise<Result> {
    return new Promise((resolve, reject) => {
      this.queue.push({ item, resolve, reject });
      this.scheduleFlush();
    });
  }

  private scheduleFlush(): void {
    // An in-flight batch already guarantees a flush when it returns.
    if (this.isInFlight) return;

    const now = Date.now();
    this.firstQueuedAt ??= now;
    const flushAt = Math.min(
      now + this.options.debounceMilliseconds,
      this.firstQueuedAt + this.options.maxDelayMilliseconds
    );

    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => this.flush(), flushAt - now);
  }

  private flush(): void {
    this.clearDebounce();
    if (this.isInFlight || this.queue.length === 0) return;

    const batch = this.queue.splice(0, this.options.maxBatchSize);
    void this.sendBatch(batch);
  }

  private async sendBatch(batch: PendingRequest<Item, Result>[]) {
    this.isInFlight = true;
    try {
      const results = await this.send(batch.map((request) => request.item));
      batch.forEach((request, index) => settle(request, results[index]));
    } catch (error) {
      batch.forEach((request) => request.reject(error));
    } finally {
      this.isInFlight = false;
      // Requests queued during the flight already waited long enough.
      this.flush();
    }
  }

  private clearDebounce(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = null;
    this.firstQueuedAt = null;
  }
}

function settle<Item, Result>(
  request: PendingRequest<Item, Result>,
  result: BatchItemResult<Result> | undefined
): void {
  if (!result) {
    request.reject(new Error("batch response is missing a result"));
  } else if (result.ok) {
    request.resolve(result.value);
  } else {
    request.reject(result.error);
  }
}
